import { useState, useEffect, useCallback, useRef } from 'react';
import {
  HOTEL_HOLD_TS_PREFIX,
  HOTEL_SESSION_EXPIRES_AT_PREFIX,
} from '../constants/hotelSession.constants';
import { SESSION_TIMER_PRODUCT } from '../constants/sessionTimer.constants';
import { getSessionRemainingMs } from '../utils/sessionTimer.utils';

function holdTsKey(sessionId) {
  return `${HOTEL_HOLD_TS_PREFIX}${sessionId}`;
}

function expiresAtKey(sessionId) {
  return `${HOTEL_SESSION_EXPIRES_AT_PREFIX}${sessionId}`;
}

function parseExpiryMs(value) {
  if (value == null || value === '') return null;
  const expiryMs = Date.parse(value) || Number(value);
  return Number.isFinite(expiryMs) ? expiryMs : null;
}

/**
 * Start hold clock once per hotel booking sessionId.
 * Re-entry / navigate guest ↔ review keeps the same deadline.
 */
export function isHotelHoldExpired(sessionId) {
  const remaining = getSessionRemainingMs({
    product: SESSION_TIMER_PRODUCT.HOTEL,
    sessionId,
  });
  return remaining === 0;
}

export function storeHotelSessionExpiresAt(sessionId, expiresAt) {
  if (!sessionId || typeof sessionStorage === 'undefined') return;
  const expiryMs = parseExpiryMs(expiresAt);
  if (expiryMs == null) return;
  sessionStorage.setItem(expiresAtKey(sessionId), new Date(expiryMs).toISOString());
}

export function ensureHotelHoldTimestamp(sessionId) {
  if (!sessionId || typeof sessionStorage === 'undefined') return;
  const key = holdTsKey(sessionId);
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, String(Date.now()));
  }
}

export function clearHotelHoldTimestamp(sessionId) {
  if (!sessionId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(holdTsKey(sessionId));
    sessionStorage.removeItem(expiresAtKey(sessionId));
  } catch (_) {
    /* ignore */
  }
}

export function clearAllHotelHoldTimestamps() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith(HOTEL_HOLD_TS_PREFIX) || k.startsWith(HOTEL_SESSION_EXPIRES_AT_PREFIX)) {
        sessionStorage.removeItem(k);
      }
    });
  } catch (_) {
    /* ignore */
  }
}

export function markHotelSessionExpired(sessionId) {
  if (typeof sessionStorage === 'undefined') return;
  const nowIso = new Date().toISOString();
  if (sessionId) {
    sessionStorage.setItem(expiresAtKey(sessionId), nowIso);
    return;
  }
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith(HOTEL_SESSION_EXPIRES_AT_PREFIX)) {
        sessionStorage.setItem(k, nowIso);
      } else if (k.startsWith(HOTEL_HOLD_TS_PREFIX)) {
        const sid = k.slice(HOTEL_HOLD_TS_PREFIX.length);
        if (sid) sessionStorage.setItem(expiresAtKey(sid), nowIso);
      }
    });
  } catch (_) {
    /* ignore */
  }
}

/**
 * Hotel booking countdown from the backend session expiry.
 * @param {string} sessionId
 * @param {() => void} [onExpired]
 */
export default function useHotelHoldTimer(sessionId, onExpired) {
  const firedRef = useRef(false);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  const getRemaining = useCallback(() => {
    if (!sessionId) return null;
    return getSessionRemainingMs({
      product: SESSION_TIMER_PRODUCT.HOTEL,
      sessionId,
    });
  }, [sessionId]);

  const [remaining, setRemaining] = useState(() => getRemaining());

  // New sessionId or first write: re-read / poll until timestamp exists
  useEffect(() => {
    firedRef.current = false;
    const left = getRemaining();
    setRemaining(left);
    if (left !== null) return undefined;

    const pollId = setInterval(() => {
      const next = getRemaining();
      if (next !== null) {
        firedRef.current = false;
        setRemaining(next);
      }
    }, 500);

    return () => clearInterval(pollId);
  }, [sessionId, getRemaining]);

  useEffect(() => {
    if (remaining === null || remaining <= 0) return undefined;

    const id = setInterval(() => {
      const left = getRemaining();
      setRemaining(left);
      if (left !== null && left <= 0 && onExpiredRef.current && !firedRef.current) {
        firedRef.current = true;
        onExpiredRef.current();
      }
    }, 1000);

    return () => clearInterval(id);
  }, [remaining !== null && remaining > 0, getRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (remaining === 0 && onExpiredRef.current && !firedRef.current) {
      firedRef.current = true;
      onExpiredRef.current();
    }
  }, [remaining]);

  if (remaining === null) {
    return {
      active: false,
      expired: false,
      totalSeconds: 0,
      label: null,
    };
  }

  const totalSeconds = Math.ceil(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return {
    active: true,
    expired: remaining <= 0,
    minutes,
    seconds,
    totalSeconds,
    label: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
  };
}
